import ErrorStackParser, { StackFrame } from "error-stack-parser";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";

import { RelodeAppConfig } from "../";
import {
    ApplicationError,
    RelodeApiError,
    RelodeErrorBase,
    telemetryService,
} from "../../modules";
import { ErrorDisplay } from "./";

interface Props extends RouteComponentProps<any> {
    config: RelodeAppConfig;
}

interface State {
    hasError: boolean;
    error?: RelodeErrorBase;
}

const parseFrames = (frames: StackFrame[]): string => {
    const stringified = frames.map(f => f.toString()).join("\n");
    return stringified;
};

const ignoredErrorMessages = ["resizeobserver loop limit exceeded"];

const errorCanBeIgnored = (message: string | Event) => {
    if (!message || typeof message !== "string") return false;
    return (
        ignoredErrorMessages.findIndex(
            m => m.toLowerCase() === message.toLowerCase()
        ) >= 0
    );
};

// const extractData = (
//     e: ErrorEvent
// ): {
//     message: string;
//     fileName: string;
//     lineno: string;
//     colno: string;
//     error: string;
// } => {
//     const error = returnString(e.error, "no error provided");
//     const message = returnString(e.message, "no message provided");

//     const data = {
//         message: message,
//         fileName: e.filename || "no filename provided",
//         lineno: e.lineno?.toString() || "no lineno provided",
//         colno: e.colno?.toString() || "no colno provided",
//         error: error,
//     };

//     return data;
// };

class ErrorBoundaryClass extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: undefined,
        };
    }

    componentDidMount() {
        window.onerror = this.handleOnError;
        window.addEventListener("error", this.handleErrorEvent);

        window.addEventListener(
            "unhandledrejection",
            this.handleUnhandledRejectionEvent
        );
    }

    componentWillUnmount() {
        window.onerror = undefined;

        window.removeEventListener("error", this.handleErrorEvent);

        window.removeEventListener(
            "unhandledrejection",
            this.handleUnhandledRejectionEvent
        );
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.location.key !== this.props.location.key) {
            this.setState({ hasError: false, error: undefined });
        }
    }

    componentDidCatch(error: Error | null, info: React.ErrorInfo) {
        let stack = "no stack trace";
        const frames = ErrorStackParser.parse(error);
        stack = parseFrames(frames);

        if (error instanceof RelodeErrorBase) {
            this.handleError(error);
        } else {
            const err = new ApplicationError(
                error
                    ? error.message
                    : "Error was swallowed during propagation."
            );
            err.origin = "react";
            err.stack = error?.stack;
            this.handleError(err);
        }
    }

    handleOnError = (m, s, l, c, e: any) => {
        if (errorCanBeIgnored(m)) return;

        /*
            m = message: error message (string). Available as event (sic!) in HTML onerror="" handler.
            s = source: URL of the script where the error was raised (string)
            l = lineno: Line number where error was raised (number)
            c = colno: Column number for the line where the error occurred (number)
            e = error: Error Object (object)
                e.message & e.name
        */

        const err = new ApplicationError(m ?? "no error message");
        err.origin = `${this.props.config.appName} External Page Error - handleOnError - swallowed`;

        if (!!e || e instanceof Error) {
            const frames = ErrorStackParser.parse(e);
            const stack = parseFrames(frames);
            err.stack = stack;
        }

        //we want to see these in lucky orange
        console.error("window.onerror", m, s, l, c, e);

        telemetryService.trackException(err);
        //telemetryService.tagBehavior("APP_ERROR");
    };

    handleErrorEvent = (e: ErrorEvent) => {
        e.stopImmediatePropagation();
        if (errorCanBeIgnored(e.message)) return;

        /*
            e = errorEvent
            e.message, e.filename, e.lineno, e.colno, e.error
        */

        if (!!e) {
            //we want to see these in lucky orange
            console.error("handleErrorEvent", e);
            const {
                message: errorEventMessage,
                filename,
                colno,
                lineno,
                error,
            } = e;
            const message = `message: ${errorEventMessage}
            filename: ${filename}
            colno: ${colno}
            lineno: ${lineno}
            error: ${error?.toString() ?? "no error found"}`;
            const err = new ApplicationError(message);
            err.origin = `${this.props?.config?.appName} External Page Error - handleErrorEvent - swallowed`;

            telemetryService.trackException(err);
            //telemetryService.tagBehavior("APP_ERROR");
        }
    };

    handleUnhandledRejectionEvent = (e: PromiseRejectionEvent) => {
        e.stopImmediatePropagation();
        e.preventDefault();

        /*
        e.promise
        e.reason - value or object indicating why the promise was rejected, as passed to Promise.reject()
    */
        const { reason: error } = e;

        if (error instanceof RelodeApiError) {
            if (error.type === "abort") return;
            this.handleError(error);
            return;
        }

        if (error instanceof ApplicationError) {
            this.handleError(error);
            return;
        }

        if (error instanceof Error) {
            // This really should catch everything else.
            const err = ApplicationError.fromError(error);
            err.origin = "handleUnhandledRejectionEvent";
            this.handleError(err);
            return;
        }

        // cannot think of why we would get here, but just to be sure...
        const err = new ApplicationError("unknown rejection error");
        err.name = "unknown rejection";
        err.type = "unknown rejection";
        try {
            err.info = JSON.stringify(error ?? "no error object to stringify");
            err.message = err.info;
        } catch {
            err.info = "unable to stringify error object";
        }
        err.origin = "handleUnhandledRejectionEvent";

        console.log("unknown rejection error handled", error, e);

        this.handleError(err);
    };

    handleError = (err: RelodeErrorBase) => {
        if (
            err instanceof ApplicationError &&
            (err.message.includes(
                "React is running in production mode, but dead code elimination has not been applied."
            ) ||
                err.message.includes("Object Not Found Matching Id"))
        ) {
            return;
        }

        if (err instanceof RelodeApiError && err.status === 401) {
            if (err.status === 401) {
                this.props.history.push("/signin", {
                    from: this.props.location,
                });
                return;
            }
        }

        //we want to see these in lucky orange
        console.error("handleError", err);

        // if the error is an abort we are just allowing those through for the time being
        this.setState({ hasError: true, error: err });
        telemetryService.trackException(err);
    };

    render() {
        const { hasError, error } = this.state;

        return hasError && error ? (
            <ErrorDisplay error={error} />
        ) : (
            <>{this.props.children}</>
        );
    }
}

export const ErrorBoundary = withRouter(ErrorBoundaryClass);
